"use client";

import { useState, useRef, useEffect, useCallback } from "react";
import Image from "next/image";
import FAIcon from "@/components/icons/faIcon";
import SectionEyebrow from "@/components/common/sectionEyebrow";
import SectionTitle from "@/components/common/sectionTitle";
import BlurFadeText from "@/components/common/blurFadeText";
import Button from "@/components/common/button";

const teamCards = [
  {
    title: "Mike Fletcher",
    role: "Managing Director",
    image: "/images/team/mike-fletcher.webp",
    alt: "SML consultants discussing advisory support",
    objectPosition: "50% center",
  },
  {
    title: "Wayne Hodgeson",
    role: "Technical Services Director",
    image: "/images/team/wayne-hodgeson.webp",
    alt: "Training session being delivered to staff",
    objectPosition: "50% center",
  },
  {
    title: "Ian Dakin",
    role: "Senior Health and Safety Consultant",
    image: "/images/team/ian-dakin.webp",
    alt: "SML consultants discussing advisory support",
    objectPosition: "50% center",
  },
  {
    title: "Heather Collins",
    role: "Fire Safety & Retail Specialist",
    image: "/images/team/heather-collins.webp",
    alt: "Training session being delivered to staff",
    objectPosition: "50% center",
  },
  {
    title: "Simon Ringham",
    role: "Operations Director",
    image: "/images/team/simon-ringham.webp",
    alt: "Consultant reviewing inspection and compliance requirements",
    objectPosition: "44% center",
  },
  {
    title: "Grant Strelling",
    role: "Commercial Director",
    image: "/images/team/grant-strelling.webp",
    alt: "SML support staff providing assistance",
    objectPosition: "50% center",
  },
];

function TeamCard({ card }) {
  return (
    <article className="group relative overflow-hidden rounded-lg bg-[#101214] h-full">
      <div className="relative min-h-104">
        <Image
          src={card.image}
          alt={card.alt}
          fill
          sizes="(max-width: 768px) 100vw, (max-width: 1280px) 50vw, 33vw"
          className="object-cover transition-transform duration-500 group-hover:scale-[1.03]"
          style={{ objectPosition: card.objectPosition }}
        />
        <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(6,9,12,0.04),rgba(6,9,12,0.12)_42%,rgba(6,9,12,0.86)_100%)]" />
      </div>

      <div className="absolute hover:scale-105 duration-300 transition-all hover:shadow-xl cursor-pointer right-5 top-5 z-10 flex h-12 w-12 items-center justify-center rounded-full bg-white/6 text-white backdrop-blur-sm">
        <Image
          src="/images/other/icons/linked-in-logo.svg"
          alt="LinkedIn logo"
          width={34}
          height={34}
        />
      </div>

      <div className="absolute inset-x-0 bottom-0 z-10 space-y-1 px-5 pb-5 sm:px-6 sm:pb-6">
        <h3 className="font-raleway text-[1.75rem] font-semibold leading-[0.94] tracking-tighter text-white sm:text-[1.95rem]">
          {card.title}
        </h3>
        <p className="text-sm leading-6 text-white/78 sm:text-[0.95rem]">
          {card.role}
        </p>
      </div>
    </article>
  );
}

function ArrowMark({ className = "" }) {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 96 96"
      className={className}
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M16 48H72M48 24L72 48L48 72"
        stroke="currentColor"
        strokeWidth="8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function FillerCard() {
  return (
    <article className="group relative overflow-hidden rounded-lg bg-primary text-[#06040c] p-8 h-full">
      <p className="font-raleway max-w-80 space-y-3 text-[2rem] font-semibold leading-none tracking-tighter text-[#06040c] sm:text-[2.45rem]">
        We take pride in our team and the work we do.
      </p>
      <div className="relative ">
        <div className="absolute inset-0" />
      </div>

      <ArrowMark className="absolute right-4 top-19 h-22 w-22 -translate-y-1/2 text-[#06040c]/10 sm:h-28 sm:w-28" />

      <div className="absolute inset-x-0 bottom-0 z-10 px-5 pb-5 sm:px-6 sm:pb-6">
        <Button
          href="/contact#contact-form"
          text={
            <>
              <span className="sm:hidden">Let&apos;s Talk</span>
              <span className="hidden sm:inline">
                Not sure where to start? Let&apos;s Talk
              </span>
            </>
          }
          variant="dark"
          endIcon={
            <span className="flex min-h-9 min-w-9 items-center justify-center rounded-full bg-white text-[#06040c]">
              <FAIcon icon="phone" size={12} className="m-0" noMargin />
            </span>
          }
        />
      </div>
    </article>
  );
}

export default function TeamSection() {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [cardStep, setCardStep] = useState(0);
  const trackRef = useRef(null);
  const cardRef = useRef(null);
  const gap = 16;

  useEffect(() => {
    const measure = () => {
      if (cardRef.current) {
        const width = cardRef.current.offsetWidth;
        setCardStep(width + gap);
      }
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [gap]);

  const totalCards = teamCards.length + 1; // +1 for filler card
  const maxIndex = Math.max(0, totalCards - 1);

  const scroll = useCallback(
    (direction) => {
      setCurrentIndex((prev) => {
        const next = prev + direction;
        return Math.max(0, Math.min(next, maxIndex));
      });
    },
    [maxIndex],
  );

  const atStart = currentIndex === 0;
  const atEnd = currentIndex >= maxIndex;

  return (
    <section className="space-y-10 py-4 sm:py-6">
      <div className="mb-12 grid gap-6 sm:mb-16 lg:mb-24 lg:grid-cols-[minmax(0,0.76fr)_minmax(280px,0.64fr)] lg:items-end">
        <div className="space-y-5 ">
          <SectionEyebrow
            text="Team"
            className="pt-0"
            textClassName="text-black/52"
          />

          <SectionTitle
            text="The people behind SML."
            className="text-[2.6rem] sm:text-[3.2rem]"
          />
        </div>

        <p className="max-w-2xl text-base leading-7 text-[#06040c]/62 sm:text-[1.05rem] sm:leading-8 lg:text-end">
          <BlurFadeText
            text={
              "Our team of consultants, trainers and support staff are the heart of SML"
            }
            delay={0.16}
            blur={8}
            y={16}
          />
        </p>
      </div>

      <div className="relative">
        {}
        <div className="rounded-lg">
          <div
            ref={trackRef}
            className="flex gap-4 transition-transform duration-500 ease-out will-change-transform"
            style={{
              transform:
                cardStep > 0
                  ? `translateX(${-currentIndex * cardStep}px)`
                  : "translateX(0)",
            }}
          >
            {teamCards.map((card, i) => (
              <div
                key={`${card.title}-${i}`}
                ref={i === 0 ? cardRef : null}
                className="shrink-0 w-[82vw] sm:w-[calc(50vw-2rem)] lg:w-[calc(33.333vw-2rem)] xl:w-[330px]"
              >
                <TeamCard card={card} />
              </div>
            ))}
            <div className="shrink-0 w-[82vw] sm:w-[calc(50vw-2rem)] lg:w-[calc(33.333vw-2rem)] xl:w-[330px]">
              <FillerCard />
            </div>
          </div>
        </div>

        {}
        <div className="mt-6 flex items-center gap-3">
          <button
            onClick={() => scroll(-1)}
            disabled={atStart}
            className="flex h-11 w-11 items-center justify-center rounded-full bg-white text-[#06040c] shadow-lg transition-all duration-300 hover:scale-105 hover:shadow-xl disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:scale-100 sm:h-13 sm:w-13"
            aria-label="Previous team member"
          >
            <FAIcon icon="chevron-left" size={18} noMargin />
          </button>
          <button
            onClick={() => scroll(1)}
            disabled={atEnd}
            className="flex h-11 w-11 items-center justify-center rounded-full bg-white text-[#06040c] shadow-lg transition-all duration-300 hover:scale-105 hover:shadow-xl disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:scale-100 sm:h-13 sm:w-13"
            aria-label="Next team member"
          >
            <FAIcon icon="chevron-right" size={18} noMargin />
          </button>
        </div>
      </div>
    </section>
  );
}
